// The business case (the investment memo's words), kept in the quote's
// `proposal` document as `memo`. Any text can carry live figures as tokens,
// {payback} or {one}, and **bold**.

export interface AskStage {
  stage: string;
  what: string;
  cost: string;
  decision: string;
}

export interface BusinessCase {
  /** The ask's headline; blank for the default from the order. */
  askLine: string;
  recommendation: string;
  stages: AskStage[];
  owner: string;
  decideBy: string;
  problem: string;
  /** One per line. */
  solution: string;
  changes: string;
  method: string;
  measureBy: string;
  checkpoints: string;
  rolesOlytic: string;
  rolesCustomer: string;
  risks: { risk: string; mitigation: string }[];
  alternatives: { option: string; cost: string; time: string; why: string }[];
  itIntro: string;
  it: { topic: string; answer: string }[];
  exit: string;
  accounting: string;
}

export const EMPTY_CASE: BusinessCase = {
  askLine: '',
  recommendation: '',
  stages: [],
  owner: '',
  decideBy: '',
  problem: '',
  solution: '',
  changes: '',
  method: '',
  measureBy: '',
  checkpoints: '',
  rolesOlytic: '',
  rolesCustomer: '',
  risks: [],
  alternatives: [],
  itIntro: '',
  it: [],
  exit: '',
  accounting: '',
};

export type CaseRowsKey = 'stages' | 'risks' | 'alternatives' | 'it';

/** The editor's columns for each table in the business case. */
export const CASE_ROWS: Record<CaseRowsKey, { key: string; label: string }[]> = {
  stages: [
    { key: 'stage', label: 'Stage' },
    { key: 'what', label: 'What happens' },
    { key: 'cost', label: 'Cost' },
    { key: 'decision', label: 'Decision' },
  ],
  risks: [
    { key: 'risk', label: 'Risk' },
    { key: 'mitigation', label: "How we'll handle it" },
  ],
  alternatives: [
    { key: 'option', label: 'Option' },
    { key: 'cost', label: 'Cost' },
    { key: 'time', label: 'Time to value' },
    { key: 'why', label: 'Why not' },
  ],
  it: [
    { key: 'topic', label: 'Topic' },
    { key: 'answer', label: 'How it works' },
  ],
};

const text = (v: unknown) => (typeof v === 'string' ? v : '');
const rows = <T>(v: unknown, keys: (keyof T)[]): T[] =>
  Array.isArray(v) ? v.map((r) => Object.fromEntries(keys.map((k) => [k, text((r as Record<string, unknown>)?.[k as string])])) as T) : [];

export function readCase(proposal: unknown): BusinessCase {
  const stored = proposal && typeof proposal === 'object' ? (proposal as { memo?: Record<string, unknown> }).memo ?? {} : {};
  const out = { ...EMPTY_CASE };
  for (const key of Object.keys(EMPTY_CASE) as (keyof BusinessCase)[]) {
    if (typeof EMPTY_CASE[key] === 'string') (out as Record<string, unknown>)[key] = text(stored[key]);
  }
  out.stages = rows<AskStage>(stored.stages, ['stage', 'what', 'cost', 'decision']);
  out.risks = rows(stored.risks, ['risk', 'mitigation']);
  out.alternatives = rows(stored.alternatives, ['option', 'cost', 'time', 'why']);
  out.it = rows(stored.it, ['topic', 'answer']);
  return out;
}

/** The tokens any business-case text can use, and what each stands for. */
export const TOKENS: Record<string, string> = {
  payback: 'payback, expected case',
  consPayback: 'payback, conservative case',
  bestPayback: 'payback, best case',
  gross: 'annual benefit',
  runNet: 'net return each year once live',
  one: 'one-time price',
  mon: 'monthly license',
  year: 'license a year',
  kickoff: 'kickoff date',
  golive: 'go-live date',
  customer: 'the customer',
  ms1: 'first payment',
  rest: 'the rest of the one-time price',
};

/** Replaces {token}s with their figures; an unknown token stays as typed. */
export function fillTokens(value: string, figures: Partial<Record<string, string>>): string {
  return value.replace(/\{(\w+)\}/g, (match, key: string) => figures[key] ?? match);
}

/** Text to runs, for **bold**. */
export function boldRuns(value: string): { text: string; bold: boolean }[] {
  return value
    .split(/\*\*(.+?)\*\*/g)
    .map((part, i) => ({ text: part, bold: i % 2 === 1 }))
    .filter((r) => r.text);
}

/** A one-per-line field as its items, without leading dashes or blanks. */
export const linesOf = (value: string) =>
  value
    .split('\n')
    .map((l) => l.replace(/^\s*[-•]\s*/, '').trim())
    .filter(Boolean);
